import React from "react";
import { Flex } from "antd";
import { useTranslate } from "@refinedev/core";
import { Grid, TextArea, Toggle } from "@libertify/ui";
import { PlaybookSelect } from "../../PlaybookSelect";
import { PlaybookFormItem } from "../../PlaybookFormItem";
import { useChatbotConfigData } from "../hooks/useChatbotConfigData";
import { ChatbotSettingRow } from "./ChatbotSettingRow";

const KEY = "createExperience.addOns.aiChat";

/** Form paths ARE the `ChatbotFunctionalSettings` field names, so the values
 *  go to the API untranslated. Listed once so the two cards can't drift. */
const FIELD = {
  answerScope: ["functional_settings", "answer_scope"],
  tone: ["functional_settings", "tone"],
  greetingMessage: ["functional_settings", "greeting_message"],
  showSources: ["functional_settings", "show_sources"],
  showSuggestedQuestions: ["functional_settings", "show_suggested_questions"],
};

export type ChatbotSettingsVariant = "create" | "edit";

interface ChatbotSettingsFieldsProps {
  /** "create" adds answer scope, tone and the greeting message, which the edit
   *  design doesn't show. Both render the toggles. */
  variant?: ChatbotSettingsVariant;
}

/**
 * AI Chat settings, shared by the create and edit cards so the fields they have
 * in common can't drift. Renders into the caller's `<Form>`: create writes to
 * the draft context, edit PATCHes the project.
 */
export const ChatbotSettingsFields: React.FC<ChatbotSettingsFieldsProps> = ({
  variant = "create",
}) => {
  const t = useTranslate();
  const { answerScopeOptions, toneOptions } = useChatbotConfigData();

  if (variant === "edit") {
    return (
      <Flex vertical gap={16}>
        <ChatbotSettingRow name={FIELD.showSources} tKey="showSources" />
        {/* TODO: no backend field behind these two yet.
        <ChatbotSettingRow name={FIELD.tailorProfile} tKey="tailorProfile" />
        */}
        <ChatbotSettingRow
          name={FIELD.showSuggestedQuestions}
          tKey="showSuggestedQuestions"
        />
        {/*
        <ChatbotSettingRow
          name={FIELD.allowTranscriptDownload}
          tKey="allowTranscriptDownload"
        />
        */}
      </Flex>
    );
  }

  return (
    <Grid columns={2} gap="lg">
      <PlaybookFormItem
        name={FIELD.answerScope}
        labelKey={`${KEY}.answerScope.label`}
      >
        <PlaybookSelect options={answerScopeOptions} />
      </PlaybookFormItem>
      <PlaybookFormItem name={FIELD.tone} labelKey={`${KEY}.tone.label`}>
        <PlaybookSelect options={toneOptions} />
      </PlaybookFormItem>

      <PlaybookFormItem
        span={2}
        name={FIELD.greetingMessage}
        labelKey={`${KEY}.greetingMessage.label`}
      >
        <TextArea
          rows={3}
          placeholder={t(`${KEY}.greetingMessage.placeholder`)}
        />
      </PlaybookFormItem>

      <PlaybookFormItem
        name={FIELD.showSources}
        labelKey={`${KEY}.showSources.label`}
        valuePropName="checked"
      >
        <Toggle size="l" />
      </PlaybookFormItem>
      <PlaybookFormItem
        name={FIELD.showSuggestedQuestions}
        labelKey={`${KEY}.showSuggestedQuestions.label`}
        valuePropName="checked"
      >
        <Toggle size="l" />
      </PlaybookFormItem>
    </Grid>
  );
};
